import {
  ComponentCategory,
  type Perfume,
  type PerfumeComponent,
  perfumeVolume,
  perfumeCountComponents,
  removeEmptyComponents,
  makeIngredient,
  allAvailableIngredients,
} from "@/models";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { PerfumeCategoryTitle } from "./PerfumeCategoryTitle";
import { useMemo } from "react";
import { FormulaNumber } from "./formulaNumber";
import { makeDiplome } from "./makeDiplome";
import { capitalizeIngredientName, useIngredientDiplayName } from "@/services";
import { IngredientPicture } from "./IngredientPicture";
import { useTranslation } from "react-i18next";

const perfumeDetailDateFormat = new Intl.DateTimeFormat("fr-FR", {
  dateStyle: "full",
});

export function PerfumeDetails({
  perfume,
  showComponents,
}: {
  perfume: Perfume;
  showComponents?: boolean;
}) {
  const volume = useMemo(() => perfumeVolume(perfume), [perfume]);
  const numberOfComponents = perfumeCountComponents(perfume);

  const componentsByCategory = useMemo(() => {
    const filterComponents = (category: ComponentCategory) =>
      removeEmptyComponents(perfume.components).filter((c) => c.category === category);

    return {
      notesDeTete: filterComponents(ComponentCategory.NOTE_DE_TETE),
      notesDeCoeur: filterComponents(ComponentCategory.NOTE_DE_COEUR),
      notesDeFond: filterComponents(ComponentCategory.NOTE_DE_FOND),
    };
  }, [perfume.components]);

  const handleDiploma = async ({ noBackground }: { noBackground: boolean }) => {
    const diplomeBlob = await makeDiplome({
      perfume,
      noBackground,
    });
    const url = URL.createObjectURL(diplomeBlob);
    window.open(url);
    URL.revokeObjectURL(url);
  };

  const { t } = useTranslation();
  return (
    <Stack sx={{ alignItems: "center", gap: 1 }}>
      <Typography variant="h3" gutterBottom>
        {perfume.name}
      </Typography>
      <Typography variant="body1">
        {t("summaryCreated", { date: perfumeDetailDateFormat.format(perfume.created) })}
      </Typography>
      <Typography variant="body1">
        {perfume.workshopLocation &&
          t("summaryLocation", { location: perfume.workshopLocation.address })}
      </Typography>
      <Typography variant="body1">
        {t("summaryFormulaNumber")} <FormulaNumber formulaNumber={perfume.formulaNumber} />
      </Typography>
      <Typography variant="body1" gutterBottom>
        {t("summaryComponentsVolume", { numberOfComponents, volume })}
      </Typography>
      {showComponents && (
        <>
          <PerfumeCategory
            category={ComponentCategory.NOTE_DE_TETE}
            components={componentsByCategory.notesDeTete}
          />
          <PerfumeCategory
            category={ComponentCategory.NOTE_DE_COEUR}
            components={componentsByCategory.notesDeCoeur}
          />
          <PerfumeCategory
            category={ComponentCategory.NOTE_DE_FOND}
            components={componentsByCategory.notesDeFond}
          />
        </>
      )}
      {/* 
      {perfume.username && (
        <Typography variant="subtitle1" gutterBottom>
          Author: {perfume.username} / {perfume.email}
        </Typography>
      )} */}
      <Button onClick={() => handleDiploma({ noBackground: false })}>{t("showDiploma")}</Button>
      <Button onClick={() => handleDiploma({ noBackground: true })}>
        {t("showDiploma")} (sans fond)
      </Button>
    </Stack>
  );
}

export function PerfumeCategory({
  category,
  components,
}: {
  category: ComponentCategory;
  components: PerfumeComponent[];
}) {
  const ingredientDisplayName = useIngredientDiplayName();

  if (components.length === 0) {
    return <></>;
  }

  return (
    <>
      <Divider flexItem sx={{ width: 1, my: 1 }}>
        <Typography color="text.secondary" variant="subtitle1">
          <PerfumeCategoryTitle category={category} quantity={components.length} />
        </Typography>
      </Divider>
      <Stack
        direction="row"
        sx={{
          width: 1,
          px: {
            xs: 1,
            sm: 4,
          },
          gap: 4,
          flexWrap: "wrap",
        }}
      >
        {components.map((component, i) => {
          // createMissingIngredient: a component whose ingredient left the catalog keeps a picture
          const [ingredient] = makeIngredient(component, allAvailableIngredients, {
            createMissingIngredient: true,
          });
          return (
            <Stack
              key={i}
              direction="row"
              sx={{
                alignItems: "center",
                gap: 1,
                flexGrow: 1,
              }}
            >
              {ingredient && <IngredientPicture ingredient={ingredient} size="icon" />}
              <Box>
                <Typography variant="h5">
                  {ingredient
                    ? ingredientDisplayName(ingredient)
                    : capitalizeIngredientName(component.name)}
                </Typography>
                <Typography variant="body1">{component.volume} ml</Typography>
              </Box>
            </Stack>
          );
        })}
      </Stack>
    </>
  );
}
